/* =================================================================
 * Birthday Blowout — SHARED BASE JS (Stage B / Liquid).
 *
 * The ONE unified cart for the landing page. Loaded once by
 * templates/page.winter-warm-up.json, BEFORE the sections render, so
 * window.PZcart exists by the time any section IIFE binds its buttons.
 *
 * Replaces the Stage-A mock (window.PZcart.add(name), which only bumped
 * a fake counter). This is the REAL Shopify AJAX cart — the Grand
 * Opening pattern:
 *
 *   CONTRACT (per the section contract): sections add to cart ONLY via
 *   window.PZcart.addVariant(variantId, qty). They must NOT POST to
 *   /cart/add.js themselves, and must NOT render their own cart bar /
 *   counter. The only shared global ids are #pz-ct, #pz-total and
 *   #pz-cartbar, all owned here.
 *
 *     addVariant(variantId, qty)  ->  POST /cart/add.js  ->  refresh()
 *     refresh()                   ->  GET  /cart.js
 *                                     -> #pz-ct    = item_count
 *                                     -> #pz-total = money(total_price)
 *                                     -> .pz-cartbar bump (tactile pulse)
 *
 * Also owns: smooth-scroll for in-page anchor jumps (the hero / footer
 * CTAs that point at #b-feed etc.), and a refresh() on DOMContentLoaded
 * so the bar shows the real cart state on first paint.
 * ================================================================= */
(function () {
  'use strict';

  // ---- money formatter -------------------------------------------------
  // Shopify /cart.js returns prices in CENTS (integer). Render ZAR with a
  // thin-spaced "R " prefix to match the locked design's price style.
  // Uses Shopify's shop money format when the theme exposes it, else a
  // safe ZAR default.
  function money(cents) {
    var amount = (Number(cents) || 0) / 100;
    var s = amount.toLocaleString('en-ZA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return 'R ' + s;
  }

  // ---- DOM helpers (the shared, base-owned ids only) -------------------
  function ctEl()    { return document.getElementById('pz-ct'); }
  function totalEl() { return document.getElementById('pz-total'); }
  function barEl()   { return document.getElementById('pz-cartbar'); }

  function bumpBar() {
    var bar = barEl();
    if (!bar) return;
    bar.classList.remove('pz-bumped');
    // force reflow so the animation re-triggers on rapid adds
    void bar.offsetWidth;
    bar.classList.add('pz-bumped');
  }

  // ---- the unified cart -----------------------------------------------
  window.PZcart = {
    // last known cart snapshot (handy for any section that wants to read it)
    item_count: 0,
    total_price: 0,

    /**
     * Add a variant to the real Shopify cart.
     * @param {number|string} variantId  numeric Shopify variant id
     * @param {number} [qty=1]           quantity to add
     * @returns {Promise} resolves with the refreshed /cart.js payload
     *
     * This is the ONLY add path sections may call. On success it refreshes
     * the single counter + total from /cart.js and pulses the bar.
     */
    addVariant: function (variantId, qty) {
      var id = parseInt(variantId, 10);
      var quantity = parseInt(qty, 10);
      if (!quantity || quantity < 1) quantity = 1;
      if (!id) {
        // never POST a bad id; surface it for the section, no fake bump
        return Promise.reject(new Error('PZcart.addVariant: missing/invalid variantId'));
      }
      var self = this;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: id, quantity: quantity })
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () { return {}; }).then(function (err) {
              var msg = (err && err.description) ? err.description : ('cart/add failed (' + res.status + ')');
              throw new Error(msg);
            });
          }
          return res.json();
        })
        .then(function () {
          // refresh from the source of truth, then pulse the bar
          return self.refresh().then(function (cart) {
            bumpBar();
            return cart;
          });
        });
    },

    /**
     * Refresh the single counter (#pz-ct) and total (#pz-total) from the
     * authoritative /cart.js. Called after every add and once on load.
     * @returns {Promise} resolves with the /cart.js payload
     */
    refresh: function () {
      var self = this;
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : { item_count: 0, total_price: 0 }; })
        .then(function (cart) {
          self.item_count = cart.item_count || 0;
          self.total_price = cart.total_price || 0;
          var c = ctEl();
          if (c) c.textContent = self.item_count;
          var t = totalEl();
          if (t) t.textContent = money(self.total_price);
          return cart;
        })
        .catch(function () {
          // network hiccup: leave the last known values rather than zeroing
          return { item_count: self.item_count, total_price: self.total_price };
        });
    },

    // expose the formatter so sections can render consistent prices if needed
    money: money
  };

  // ---- smooth-scroll for in-page anchor jumps --------------------------
  // Hero/footer CTAs are <a href="#b-feed"> etc. CSS already sets
  // scroll-behavior:smooth, but we intercept so the click also offsets for
  // the sticky ribbon and works even if a section re-renders the anchor.
  function onAnchorClick(e) {
    var a = e.target.closest('a[href^="#"]');
    if (!a) return;
    var hash = a.getAttribute('href');
    if (!hash || hash === '#') return;
    var target = document.querySelector(hash);
    if (!target) return;                       // unknown anchor -> let it be
    e.preventDefault();
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }

  // ---- boot ------------------------------------------------------------
  function boot() {
    document.addEventListener('click', onAnchorClick);
    window.PZcart.refresh();                    // show real cart state on first paint
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
